// AB#1749 — Calendar Week view (mockup §B of Calendar v1 Epic AB#1746).
// Fires 7 parallel `useCalendarGridQuery` calls (Sun..Sat or Mon..Sun,
// driven by the business's week-start preference). Each day query is
// independently cached and SignalR-invalidated via `calendar.all`, so
// the Week view stays in sync with the Day view and the booking flow
// for free.
//
// No new BE endpoint needed — works on top of the deployed
// `GET /salon-owner/calendar/grid?branchId=&date=` contract from C3.

import { useQueries } from '@tanstack/react-query';
import { apiService } from '../services/api';
import { queryKeys } from '../lib/queryKeys';
import type { CalendarGridResponse } from '../types/api-responses';

export interface UseCalendarWeekParams {
    branchId: number | null;
    /** "yyyy-MM-dd" — the FIRST day of the visible week (e.g. Sun 17). */
    weekStart: string;
}

export interface CalendarWeekDay {
    /** "yyyy-MM-dd". */
    date: string;
    /** Loaded grid for that day, or null while loading / on error. */
    grid: CalendarGridResponse | null;
    isLoading: boolean;
    isError: boolean;
}

export interface CalendarWeekResult {
    days: CalendarWeekDay[];
    isAnyLoading: boolean;
    isAnyError: boolean;
}

function addDaysIso(iso: string, days: number): string {
    const d = new Date(`${iso}T00:00:00`);
    d.setDate(d.getDate() + days);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
}

/**
 * Fetch the 7-day grid for the Week view. Each day is queried separately
 * so that a single failing day doesn't blank the whole week, and so that
 * the Day-view cache and Week-view cache share entries (open Day-view
 * for Wed, then switch to Week-view → Wed's data is already there).
 */
export function useCalendarWeekQuery(params: UseCalendarWeekParams): CalendarWeekResult {
    const dates = Array.from({ length: 7 }, (_, i) => addDaysIso(params.weekStart, i));

    const results = useQueries({
        queries: dates.map((date) => ({
            queryKey: queryKeys.calendar.grid({
                branchId: params.branchId ?? 0,
                date,
            }),
            queryFn: () => apiService.getCalendarGrid({
                branchId: params.branchId as number,
                date,
            }),
            enabled: params.branchId != null && params.branchId > 0,
            staleTime: 30_000,
        })),
    });

    const days: CalendarWeekDay[] = results.map((r, i) => ({
        date: dates[i],
        grid: (r.data as CalendarGridResponse | undefined) ?? null,
        isLoading: r.isLoading,
        isError: r.isError,
    }));

    return {
        days,
        isAnyLoading: results.some((r) => r.isLoading),
        isAnyError: results.some((r) => r.isError),
    };
}
